import { nanoid } from 'nanoid';
import {
  db,
  getPendingSyncEvents,
  markSyncEventsComplete,
  bulkUpsert,
  enqueueSyncEvent,
} from './db';
import {
  capabilitiesApi,
  graphApi,
  collectionsApi,
  ApiRequestError,
} from './api';
import type {
  SyncEvent,
  SyncOperation,
  Capability,
  CapabilityNode,
  Edge,
  Collection,
} from '@/types';

// ─── Sync engine state ────────────────────────────────────────────────────────

export type SyncEngineStatus = 'idle' | 'syncing' | 'error' | 'offline';

type SyncStatusListener = (status: SyncEngineStatus, error?: string) => void;

class SyncEngine {
  private _status: SyncEngineStatus = 'idle';
  private _listeners: Set<SyncStatusListener> = new Set();
  private _syncTimer: ReturnType<typeof setTimeout> | null = null;
  private _isSyncing = false;

  readonly AUTO_SYNC_INTERVAL_MS = 30_000; // 30 seconds

  get status(): SyncEngineStatus {
    return this._status;
  }

  // ─── Listener management ────────────────────────────────────────────────────

  subscribe(listener: SyncStatusListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  private notify(status: SyncEngineStatus, error?: string): void {
    this._status = status;
    this._listeners.forEach((l) => l(status, error));
  }

  // ─── Enqueue outbound changes ─────────────────────────────────────────────

  async enqueue(
    workspaceId: string,
    entityType: SyncEvent['entityType'],
    entityId: string,
    operation: SyncOperation,
    payload: Record<string, unknown>
  ): Promise<void> {
    const event: SyncEvent = {
      id: nanoid(),
      workspaceId,
      entityType,
      entityId,
      operation,
      payload,
      status: 'pending',
      retries: 0,
      error: null,
      createdAt: new Date().toISOString(),
      syncedAt: null,
    };
    await enqueueSyncEvent(event);
    this.scheduleSyncDebounced();
  }

  // ─── Auto-sync scheduling ──────────────────────────────────────────────────

  startAutoSync(workspaceId: string): void {
    this.stopAutoSync();
    this._syncTimer = setInterval(() => {
      this.sync(workspaceId).catch((err) => {
        console.error('Auto-sync failed:', err);
      });
    }, this.AUTO_SYNC_INTERVAL_MS);
  }

  stopAutoSync(): void {
    if (this._syncTimer !== null) {
      clearInterval(this._syncTimer);
      this._syncTimer = null;
    }
  }

  private scheduleSyncDebounced(): void {
    // Will be debounced by the 500ms delay in setTimeout
    setTimeout(() => {
      // No-op: actual sync happens via startAutoSync or manual trigger
    }, 500);
  }

  // ─── Pull remote → local ───────────────────────────────────────────────────

  async pullFromRemote(workspaceId: string): Promise<void> {
    const [capResult, nodesResult, edgesResult, collectionsResult] = await Promise.allSettled([
      capabilitiesApi.list(workspaceId, { pageSize: 200 }),
      graphApi.getNodes(workspaceId),
      graphApi.getEdges(workspaceId),
      collectionsApi.list(workspaceId),
    ]);

    if (capResult.status === 'fulfilled') {
      await bulkUpsert<Capability>(db.capabilities, capResult.value.items);
    }
    if (nodesResult.status === 'fulfilled') {
      await bulkUpsert<CapabilityNode>(db.nodes, nodesResult.value);
    }
    if (edgesResult.status === 'fulfilled') {
      await bulkUpsert<Edge>(db.edges, edgesResult.value);
    }
    if (collectionsResult.status === 'fulfilled') {
      await bulkUpsert<Collection>(db.collections, collectionsResult.value);
    }
  }

  // ─── Push local → remote ──────────────────────────────────────────────────

  async pushToRemote(workspaceId: string): Promise<void> {
    const pending = await getPendingSyncEvents(workspaceId);
    if (pending.length === 0) {
      return;
    }

    const successful: string[] = [];
    const failed: Array<{ event: SyncEvent; error: string }> = [];

    for (const event of pending) {
      try {
        await this.applyRemoteOperation(workspaceId, event);
        successful.push(event.id);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        failed.push({ event, error: message });
      }
    }

    if (successful.length > 0) {
      await markSyncEventsComplete(successful);
    }

    // Mark failed events with error + increment retries
    for (const { event, error } of failed) {
      const maxRetries = 3;
      const newRetries = event.retries + 1;
      await db.syncQueue.update(event.id, {
        retries: newRetries,
        error,
        status: newRetries >= maxRetries ? 'error' : 'pending',
      });
    }

    if (failed.length > 0 && successful.length === 0) {
      throw new Error(`${failed.length} sync operations failed`);
    }
  }

  private async applyRemoteOperation(
    workspaceId: string,
    event: SyncEvent
  ): Promise<void> {
    const { entityType, entityId, operation, payload } = event;

    if (entityType === 'capability') {
      if (operation === 'create') {
        await capabilitiesApi.create(workspaceId, payload as Parameters<typeof capabilitiesApi.create>[1]);
      } else if (operation === 'update') {
        await capabilitiesApi.update(workspaceId, entityId, payload as Parameters<typeof capabilitiesApi.update>[2]);
      } else if (operation === 'delete') {
        await capabilitiesApi.delete(workspaceId, entityId);
      }
    } else if (entityType === 'edge') {
      if (operation === 'create') {
        await graphApi.createEdge(workspaceId, payload as Parameters<typeof graphApi.createEdge>[1]);
      } else if (operation === 'update') {
        await graphApi.updateEdge(workspaceId, entityId, payload as Parameters<typeof graphApi.updateEdge>[2]);
      } else if (operation === 'delete') {
        await graphApi.deleteEdge(workspaceId, entityId);
      }
    } else if (entityType === 'collection') {
      if (operation === 'create') {
        await collectionsApi.create(workspaceId, payload as Parameters<typeof collectionsApi.create>[1]);
      } else if (operation === 'update') {
        await collectionsApi.update(workspaceId, entityId, payload as Parameters<typeof collectionsApi.update>[2]);
      } else if (operation === 'delete') {
        await collectionsApi.delete(workspaceId, entityId);
      }
    }
  }

  // ─── Conflict resolution ───────────────────────────────────────────────────

  resolveConflict<T extends { updatedAt: string }>(
    localRecord: T,
    remoteRecord: T
  ): T {
    // Last-write-wins strategy based on updatedAt timestamp
    const localTime = new Date(localRecord.updatedAt).getTime();
    const remoteTime = new Date(remoteRecord.updatedAt).getTime();
    return remoteTime >= localTime ? remoteRecord : localRecord;
  }

  // ─── Full sync ────────────────────────────────────────────────────────────

  async sync(workspaceId: string): Promise<void> {
    if (this._isSyncing) {
      return;
    }

    if (!navigator.onLine) {
      this.notify('offline');
      return;
    }

    this._isSyncing = true;
    this.notify('syncing');

    try {
      await this.pushToRemote(workspaceId);
      await this.pullFromRemote(workspaceId);
      this.notify('idle');
    } catch (err) {
      const message = err instanceof ApiRequestError
        ? err.message
        : err instanceof Error
        ? err.message
        : 'Sync failed';

      // Suppress low-level IndexedDB/Dexie initialization errors that fire on
      // the first sync when the local DB is being opened for the first time.
      // These are transient — the DB will be ready on the next sync cycle.
      const isDbInitError =
        message.includes('onerror') ||
        message.includes('Cannot set properties of undefined') ||
        message.includes('IDBDatabase') ||
        message.includes('IndexedDB');

      if (!isDbInitError) {
        this.notify('error', message);
      } else {
        // DB not ready yet — silently reset so the next cycle can retry.
        this.notify('idle');
        console.warn('[sync] DB init error (transient, will retry):', message);
      }
    } finally {
      this._isSyncing = false;
    }
  }
}

// ─── Singleton export ─────────────────────────────────────────────────────────

export const syncEngine = new SyncEngine();
